import type { Cookies } from 'types/cookies';
import { NOTICE_BEHAVIOR_COOKIE, WEBVIEW_AMETHYST_SESSION_ID } from 'constants/cookies';

/*
  Takes cookie object(like state.cookies) and returns it into a cookie string that could be used for Cookie headers
  Example param: {at-main: '123'};
  Example return: 'at-main=123;';
*/
export const cookieObjectToString = (obj: { [x: string]: string } = {}) =>
  Object.keys(obj)
    .map(v => obj[v] && `${v}=${obj[v]}; `)
    .join('')
    .trim();

/**
 * The mobile apps (iOS/Android) set the `webview-amethyst-session-id` cookie on
 * `.zappos.com` before loading a page inside a WebView. Use this to detect
 * WebView traffic so we can suppress web-only UI (e.g. the TrustArc cookie
 * consent banner) that the apps already handle at the OS level.
 */
export const isWebViewRequest = (cookies?: Cookies): boolean => !!cookies?.[WEBVIEW_AMETHYST_SESSION_ID];

/**
 * The mobile apps load /federated-login?disableHf=true to get a chrome-free
 * login screen. Use this to suppress web-only UI (e.g. the TrustArc cookie
 * consent banner) alongside the header/footer.
 */
export const isDisableHfRequest = (query?: Record<string, string>): boolean => query?.disableHf === 'true';

/**
 * TrustArc sets the `notice_behavior` cookie to a comma-separated list of tokens
 * (e.g. `implied,eu`). An `eu` token means the visitor's consent is handled by
 * TrustArc's own geo-served GDPR experience rather than the Tollhouse-backed opt-out
 * toggle. Use this to suppress the privacy UI we inject (the cookie consent banner and
 * the "Your Privacy Choices" footer link), which would otherwise duplicate or conflict
 * with the consent surface EU users already get from TrustArc.
 */
export const isEuUser = (cookies?: Cookies): boolean => (cookies?.[NOTICE_BEHAVIOR_COOKIE] || '').split(',').includes('eu');
